import { useMemo, useState } from "react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Skeleton } from "@/components/ui/skeleton"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { useToolCatalog } from "@/features/admin/hooks/use-tool-catalog"
import type { ToolCatalogItem } from "@/features/admin/types"
import { useAuth } from "@/features/auth/hooks/useAuth"
import { BookOpen, ChevronDown, Lock, Search, Wrench } from "lucide-react"

import { ToolTutorialSheet } from "./tool-tutorial-sheet"

interface ToolCatalogCardProps {
  tool: ToolCatalogItem
  onOpenTutorial: () => void
}

/**
 * Read-only catalog card: shows what a tool does and its capabilities, without
 * the agent-scoped enable toggle or JSON config (those live on the agent tab).
 */
function ToolCatalogCard({ tool, onOpenTutorial }: ToolCatalogCardProps) {
  return (
    <Collapsible>
      <div className="rounded-lg border">
        <CollapsibleTrigger className="hover:bg-muted/50 flex w-full items-center justify-between gap-3 p-4">
          <div className="flex-1 space-y-1.5 text-left">
            <div className="flex flex-wrap items-center gap-2">
              <h4 className="font-medium">{tool.label}</h4>
              {tool.category && (
                <Badge variant="outline" className="text-muted-foreground font-normal">
                  {tool.category}
                </Badge>
              )}
              {tool.required_feature_flag && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge
                      variant="outline"
                      className="gap-1 border-amber-500/40 font-normal text-amber-600"
                    >
                      <Lock className="h-3 w-3" />
                      Requer: {tool.required_feature_flag}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>
                    Esta ferramenta exige a flag "{tool.required_feature_flag}" habilitada na
                    organização.
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
            <p className="text-muted-foreground text-sm">{tool.summary || tool.description}</p>
          </div>
          <div className="flex items-center gap-2">
            {tool.has_tutorial && (
              <Button
                size="sm"
                variant="ghost"
                className="h-8 px-2"
                onClick={e => {
                  e.stopPropagation()
                  onOpenTutorial()
                }}
              >
                <BookOpen className="mr-1 h-3.5 w-3.5" />
                Tutorial
              </Button>
            )}
            <ChevronDown className="h-4 w-4 shrink-0" />
          </div>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="bg-muted/30 space-y-4 border-t p-4">
            {tool.description && tool.description !== tool.summary && (
              <p className="text-muted-foreground text-sm">{tool.description}</p>
            )}
            {tool.actions.length > 0 ? (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Capacidades</Label>
                <ul className="space-y-1">
                  {tool.actions.map(action => (
                    <li key={action.name} className="text-muted-foreground text-xs">
                      <code className="bg-muted rounded px-1 py-0.5">{action.name}</code> -{" "}
                      {action.description}
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <p className="text-muted-foreground text-xs">
                Nenhuma capacidade detalhada para esta ferramenta.
              </p>
            )}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  )
}

/**
 * Standalone catalog of every agent tool - a reference page so users can see what
 * each tool does and read its tutorial without opening a specific agent.
 */
export function ToolCatalogOverview() {
  const { user } = useAuth()
  const { data: catalog, isLoading, isError } = useToolCatalog(user?.organization_id)

  const [search, setSearch] = useState("")
  const [tutorialTool, setTutorialTool] = useState<ToolCatalogItem | null>(null)

  const groupedTools = useMemo(() => {
    const query = search.trim().toLowerCase()
    const matches = (tool: ToolCatalogItem) =>
      !query ||
      tool.label.toLowerCase().includes(query) ||
      tool.summary.toLowerCase().includes(query) ||
      tool.description.toLowerCase().includes(query) ||
      tool.category.toLowerCase().includes(query) ||
      tool.actions.some(
        action =>
          action.name.toLowerCase().includes(query) ||
          action.description.toLowerCase().includes(query)
      )

    const groups = new Map<string, ToolCatalogItem[]>()
    for (const tool of catalog ?? []) {
      if (!matches(tool)) continue
      const category = tool.category || "Outros"
      if (!groups.has(category)) groups.set(category, [])
      groups.get(category)!.push(tool)
    }
    return Array.from(groups.entries())
  }, [catalog, search])

  const totalVisible = groupedTools.reduce((sum, [, tools]) => sum + tools.length, 0)

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Wrench className="h-5 w-5" />
          Catálogo de Ferramentas
        </CardTitle>
        <div className="relative max-w-sm pt-2">
          <Search className="text-muted-foreground absolute top-1/2 left-2.5 h-4 w-4 -translate-y-1/2" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Buscar ferramenta ou capacidade..."
            className="pl-8"
          />
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {isLoading && (
          <div className="space-y-3">
            {[0, 1, 2].map(i => (
              <Skeleton key={i} className="h-20 w-full rounded-lg" />
            ))}
          </div>
        )}

        {isError && (
          <p className="text-destructive text-sm">
            Não foi possível carregar as ferramentas. Tente novamente.
          </p>
        )}

        {!isLoading && !isError && totalVisible === 0 && (
          <p className="text-muted-foreground text-sm">
            {search ? "Nenhuma ferramenta corresponde à busca." : "Nenhuma ferramenta disponível."}
          </p>
        )}

        {groupedTools.map(([category, tools]) => (
          <div key={category} className="space-y-3">
            <h3 className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
              {category}
            </h3>
            <div className="space-y-3">
              {tools.map(tool => (
                <ToolCatalogCard
                  key={tool.name}
                  tool={tool}
                  onOpenTutorial={() => setTutorialTool(tool)}
                />
              ))}
            </div>
          </div>
        ))}
      </CardContent>

      <ToolTutorialSheet
        toolName={tutorialTool?.name ?? null}
        toolLabel={tutorialTool?.label ?? ""}
        open={!!tutorialTool}
        onOpenChange={open => {
          if (!open) setTutorialTool(null)
        }}
      />
    </Card>
  )
}
